import React, { useEffect, useState } from "react";
import {
    Box,
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    TextField,
    //Typography
} from "@mui/material";
import { GridSortModel, GridFilterModel } from "@mui/x-data-grid";
import { DataGrid, GridColDef } from "@mui/x-data-grid";
import LayoutAdmin from "../components/LayoutAdmin.tsx";
import axiosExt from '../axios.tsx';
import { TaxTypeMapping } from "../types/index.ts";
import { API_BASE_URL } from "../config.ts";
import { useTranslation } from "react-i18next";

const TaxTypeMappingPage: React.FC = () => {
    const { t } = useTranslation();

    const [items, setItems] = useState<TaxTypeMapping[]>([]);
    const [open, setOpen] = useState(false);
    const [selectedItem, setSelectedItem] = useState<TaxTypeMapping>({
        id: "",
        customsTaxTypeCode: "",
        customsTaxTypeName: "",
        alternateTaxTypeName:"",
        accountingTaxCode: "",
        accountingTaxName: "",
    });

    const [confirmDeleteOpen, setConfirmDeleteOpen] = useState(false);
    const [itemToDelete, setItemToDelete] = useState<string | null>(null);

    const [sortModel, setSortModel] = useState<GridSortModel>([]);
    const [filterModel, setFilterModel] = useState<GridFilterModel>({ items: [] });

    const savedSortKey = "sortTaxTypeMappingsList";
    const savedFilterKey = "filterTaxTypeMappingsList";

    useEffect(() => {
        const savedSortModel = JSON.parse(localStorage.getItem(savedSortKey) ?? "[]");
        const savedFilterModel = (() => {
            try {
                const parsed = JSON.parse(localStorage.getItem(savedFilterKey) ?? "{}");
                return parsed && parsed.items ? parsed : { items: [] };
            } catch (error) {
                console.error("Error parsing savedFilterKey:", error);
                return { items: [] }; // Fallback
            }
        })();
        setSortModel(savedSortModel);
        setFilterModel(savedFilterModel);

        fetchItems();
    }, []);

    // Save sortModel changes to localStorage
    const handleSortModelChange = (newModel: GridSortModel) => {
        setSortModel(newModel);
        localStorage.setItem(savedSortKey, JSON.stringify(newModel));
    };

    // Save filterModel changes to localStorage
    const handleFilterModelChange = (newModel: GridFilterModel) => {
        setFilterModel(newModel);
        localStorage.setItem(savedFilterKey, JSON.stringify(newModel));
    };

    const fetchItems = async () => {
        const token = localStorage.getItem("token");
        const response = await axiosExt.get(`${API_BASE_URL}/TaxTypeMappings`, {
            headers: { Authorization: `Bearer ${token}` },
        });
        setItems(response.data);
    };

    const handleSave = async () => {
        const token = localStorage.getItem("token");
        //const payload = { name, colorRGB };

        if (selectedItem.id) {
            await axiosExt.put(`${API_BASE_URL}/TaxTypeMappings/${selectedItem.id}`, selectedItem, {
                headers: { Authorization: `Bearer ${token}` },
            });
        } else {
            selectedItem.id = crypto.randomUUID();
            await axiosExt.post(`${API_BASE_URL}/TaxTypeMappings`, selectedItem, {
                headers: { Authorization: `Bearer ${token}` },
            });
        }

        setOpen(false);
        fetchItems();
    };

    /*const handleDelete = async (id: number) => {
        const token = localStorage.getItem("token");
        await axiosExt.delete(`${API_BASE_URL}/TaxTypeMappings/${id}`, {
            headers: { Authorization: `Bearer ${token}` },
        });
        fetchItems();
    };*/

    const handleDelete = async (id: string) => {
        try {
            setItemToDelete(id);
            setConfirmDeleteOpen(true);
        } catch (error) {
            console.error("Error deleting tax type mapping:", error);
        }
    };

    const handleConfirmDelete = async () => {
        if (itemToDelete === null) return;
        try {
            const token = localStorage.getItem("token");
            await axiosExt.delete(`${API_BASE_URL}/TaxTypeMappings/${itemToDelete}`, {
                headers: {
                    Authorization: `Bearer ${token}`,
                },
            });
            fetchItems();
        } catch (error) {
            console.error("Error deleting:", error);
        } finally {
            setConfirmDeleteOpen(false);
            setItemToDelete(null);
        }
    };

    const handleCancelDelete = () => {
        setConfirmDeleteOpen(false);
        setItemToDelete(null);
    };


    const handleEdit = (item: TaxTypeMapping) => {
        setSelectedItem(item);
        setOpen(true);
    };

    const columns: GridColDef[] = [
        { field: "customsTaxTypeCode", headerName: t("Customs Tax Type Code"), width: 150 },
        { field: "customsTaxTypeName", headerName: t("Customs Tax Type Name"), width: 150 },
        { field: "alternateTaxTypeName", headerName: t("Alternate Tax Type Name"), width: 150 },
        { field: "accountingTaxCode", headerName: t("Accounting Tax Code"), width: 150 },
        { field: "accountingTaxName", headerName: t("Accounting Tax Name"), width: 150 },
        {
            field: "actions",
            headerName: t("Actions"),
            width: 250,
            renderCell: (params) => (
                <>
                    <Button variant="contained" onClick={() => handleEdit(params.row)}>{t("Edit")}</Button>
                    <Button
                        variant="contained"
                        color="error"
                        onClick={() => handleDelete(params.row.id)}
                        style={{ marginLeft: 10 }}
                    >
                        {t("Delete")}
                    </Button>
                </>
            ),
        },
    ];

    return (
        <LayoutAdmin>
            <div style={{ height: "100%" }}>
                <div style={{ width: "100%", display: "flex" }}>
                    <h2>{t("Tax Type Mappings")}</h2>
                    <Button
                        variant="contained"
                        color="primary"
                        onClick={() => {
                            setSelectedItem({
                                id:null,
                                customsTaxTypeCode: "",
                                customsTaxTypeName: "",
                                accountingTaxCode: "",
                                accountingTaxName: "",
                            });
                            setOpen(true);
                        }}
                        style={{ marginBottom: "20px", marginTop: "20px", marginLeft: "20px" }}
                    >
                        {t("Add New")}
                    </Button>
                </div>
                <Box sx={ {width: "100%" }}>
                    <DataGrid rows={items} columns={columns}
                        sortModel={sortModel}
                        onSortModelChange={handleSortModelChange}
                        filterModel={filterModel}
                        onFilterModelChange={handleFilterModelChange}
                        initialState={{
                            pagination: {
                                paginationModel: { pageSize: 25, page: 0 },
                            },
                        }}
                        pageSizeOptions={[25, 50, 100, 200]}
                    />
                </Box>

                <Dialog open={open} onClose={() => setOpen(false)}>
                    <DialogTitle>{selectedItem ? t("Edit") : t("Add")}</DialogTitle>
                    <DialogContent>
                        <TextField
                            label={t("Customs Tax Type Code")}
                            fullWidth
                            value={selectedItem?.customsTaxTypeCode || ""}
                            onChange={(e) => setSelectedItem((prev) =>
                                prev
                                    ? { ...prev, customsTaxTypeCode: e.target.value }
                                    : { customsTaxTypeCode: e.target.value, id: "", customsTaxTypeName:"",accountingTaxCode: "", accountingTaxName: "" } // Provide a valid default object
                            )}
                            margin="dense"
                        />
                        <TextField
                            label={t("Customs Tax Type Name")}
                            fullWidth
                            value={selectedItem?.customsTaxTypeName || ""}
                            onChange={(e) => setSelectedItem((prev) =>
                                prev
                                    ? { ...prev, customsTaxTypeName: e.target.value }
                                    : { customsTaxTypeName: e.target.value, id: "", customsTaxTypeCode: "", accountingTaxCode: "", accountingTaxName: "" } // Provide a valid default object
                            )}
                            margin="dense"
                        />
                        <TextField
                            label={t("Alternate Tax Type Name")}
                            fullWidth
                            value={selectedItem?.alternateTaxTypeName || ""}
                            onChange={(e) => setSelectedItem((prev) =>
                                prev
                                    ? { ...prev, alternateTaxTypeName: e.target.value }
                                    : { customsTaxTypeName: e.target.value, id: "", customsTaxTypeCode: "", accountingTaxCode: "", accountingTaxName: "" } // Provide a valid default object
                            )}
                            margin="dense"
                        />
                        <TextField
                            label={t("Accounting Tax Code")}
                            fullWidth
                            value={selectedItem?.accountingTaxCode || ""}
                            onChange={(e) => setSelectedItem((prev) =>
                                prev
                                    ? { ...prev, accountingTaxCode: e.target.value }
                                    : { accountingTaxCode: e.target.value, id: "", customsTaxTypeCode: "", customsTaxTypeName: "", accountingTaxName: "" } // Provide a valid default object
                            )}
                            margin="dense"
                        />
                        <TextField
                            label={t("Accounting Tax Name")}
                            fullWidth
                            value={selectedItem?.accountingTaxName || ""}
                            onChange={(e) => setSelectedItem((prev) =>
                                prev
                                    ? { ...prev, accountingTaxName: e.target.value }
                                    : { accountingTaxName: e.target.value, id: "", customsTaxTypeCode: "", customsTaxTypeName: "", accounaccountingTaxCodetingTaxName: "" } // Provide a valid default object
                            )}
                            margin="dense"
                        />
                        
                    </DialogContent>
                    <DialogActions>
                        <Button onClick={() => setOpen(false)}>{t("Cancel")}</Button>
                        <Button onClick={handleSave}>{t("Save")}</Button>
                    </DialogActions>
                </Dialog>

                <Dialog open={confirmDeleteOpen} onClose={handleCancelDelete}>
                    <DialogTitle>{t("Confirm Deletion")}</DialogTitle>
                    <DialogContent>{t("Are you sure you want to delete this record?")}</DialogContent>
                    <DialogActions>
                        <Button onClick={handleCancelDelete} color="secondary">{t("no")}</Button>
                        <Button onClick={handleConfirmDelete} color="error">{t("yes")}</Button>
                    </DialogActions>
                </Dialog>
            </div>
        </LayoutAdmin>
    );
};

export default TaxTypeMappingPage;
